/* eslint-disable react/no-danger */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

// Components
import { EventsOutlined as CalendarExtraLarge } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/EventsOutlined";
import { TickOutlined as TickIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import ContactDetailsBox from "../contact-details-box/ContactDetailsBox";
import Timeline from "../timeline/Timeline";
import { MonetateContext } from "../../context/MonetateContext";

// Styles
import styles from "./contact-us-refunds-solution.module.scss";

function ContactUsRefundsSolution({
  solution: { id, fields, buttonType, hierarchyPosition },
  className,
  contactMethods,
}) {
  const { liveChatProminenceExperiment } = useContext(MonetateContext);

  const locationOnPage = hierarchyPosition.join(" | ");

  const getFieldContent = fieldName => {
    const field = fields.find(f => f.name === fieldName);
    return field ? field.content : null;
  };

  const plainContent = getFieldContent("plainContent");
  const overTwoWeeksContent = getFieldContent("overTwoWeeksContent");
  const withinTwoWeeksContent = getFieldContent("withinTwoWeeksContent");

  return (
    <article className={classnames(styles.refundsSolution, className)} data-solution-id={id}>
      <div className={styles["refundsSolution-content"]}>
        {["refundsJL", "refundsOverTwoWeeks"].includes(id) && (
          <div className={styles["refundsSolution__icon-container"]}>
            <CalendarExtraLarge />
          </div>
        )}
        <h3 className={styles.refundsSolution__top}>{getFieldContent("title")}</h3>
        <div className={styles.refundsSolution__inner} data-test="solution-inner">
          {plainContent && (
            <div
              className={styles["refundsSolution__plain-content"]}
              dangerouslySetInnerHTML={{ __html: plainContent }}
            />
          )}
          {withinTwoWeeksContent && (
            <div className={styles["refundsSolution__within-two-weeks"]}>
              {withinTwoWeeksContent.trackLink ? (
                <SecondaryButton
                  className={styles["refundsSolution__within-two-weeks__track-button"]}
                  tag="a"
                  href={withinTwoWeeksContent.trackLink}
                  target="_blank"
                >
                  Track your return
                </SecondaryButton>
              ) : null}
              <h4 className={styles["refundsSolution__within-two-weeks__subtitle"]}>
                {withinTwoWeeksContent.subtitle}
              </h4>
              <Timeline steps={withinTwoWeeksContent.steps} />
              <div className={styles["refundsSolution__within-two-weeks__gift-card"]}>
                <h4 className={styles["refundsSolution__within-two-weeks__gift-card-title"]}>Your refund method</h4>
                <p>
                  You can view your refund method through the order details link in your{" "}
                  <strong>Return Processed email</strong>.
                </p>
              </div>
            </div>
          )}
          {overTwoWeeksContent && (
            <div>
              <p>
                <strong>{overTwoWeeksContent.subtitle}</strong>
              </p>
              <div>
                {overTwoWeeksContent.infoBlocks.map((infoBlock, i) => (
                  <div key={i} className={styles["refundsSolution__info-block"]}>
                    <div>
                      <TickIcon aria-hidden="true" />
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: infoBlock.content }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {liveChatProminenceExperiment ? (
            <ContactDetailsBox
              title="We're here to help"
              fields={fields.filter(({ name }) => ["phone", "chat", "email"].includes(name))}
              buttonType={buttonType}
              solutionId={id}
              location={locationOnPage}
              viewContactDetails
              showGreyBackground
            />
          ) : (
            <ContactDetailsBox
              newDesign
              title="Did this answer your question?"
              leftButtonText="No, I need help"
              rightButtonText="Yes, that‘s helpful"
              fields={fields.filter(({ name }) => ["phone", "chat", "email"].includes(name))}
              buttonType={buttonType}
              solutionId={id}
              location={locationOnPage}
              otherContactDetails={contactMethods}
            />
          )}
        </div>
      </div>
    </article>
  );
}

ContactUsRefundsSolution.propTypes = {
  solution: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        content: PropTypes.string,
      }),
    ),
    buttonType: PropTypes.string,
    hierarchyPosition: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  className: PropTypes.string,
  contactMethods: PropTypes.shape({
    emailUs: PropTypes.arrayOf(PropTypes.string),
    liveChat: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ name: PropTypes.string, pct: PropTypes.number })]),
    ),
    partnersLiveChat: PropTypes.arrayOf(PropTypes.string),
  }),
};

ContactUsRefundsSolution.defaultProps = {
  className: undefined,
  contactMethods: {},
};

export default ContactUsRefundsSolution;
